<script setup>
import {computed, ref, watch, onMounted} from 'vue'
import {useStore} from 'vuex'
import {useRouter} from 'vue-router'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { getPlaceDetails } from '@/utils/nominatim'
import { generateDisplayName } from '@/utils/user'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select"
import { flattenProjectsWithLevel } from '@/utils/projectTree'
import { Spinner } from '@/components/ui/spinner'
import MemberSearchInput from '@/components/member/MemberSearchInput.vue'
import MemberInviteList from '@/components/member/MemberInviteList.vue'
import MemberManualEntryForm from '@/components/member/MemberManualEntryForm.vue'

import { FeatureSelection } from '@/components/feature-selection'
import ColorPicker from '@/components/ui/ColorPicker.vue'
import EmojiPicker from '@/components/ui/EmojiPicker.vue'
import { autoSimplifyFeature } from '@/utils/geometry'

const router = useRouter()

const props = defineProps({
  project: {
    type: Object,
    default: null
  },
  edit: {
    type: Boolean,
    default: false
  }
})

const formRef = ref(null)
const isRegion = ref(false)
const currentBoundary = ref(null)
const boundaryColor = ref('#0059ff')
const isSubmiting = ref(false)
const selectedGeometry = ref(null)
const searchAddedMembers = ref([])

const store = useStore()
const orgs = computed(() => store.getters['org/getOrgs'])
const flatedProjects = computed(() => store.getters['project/getFlatedProjects'])
const treeProjects = computed(() => flattenProjectsWithLevel(
  (flatedProjects.value || []).filter(i => i.organization_id === props.project?.organization_id)
))

const member = computed(() => store.getters['user/getMember'])
const currentOrgId = computed(() => props.project?.organization_id)

const PROJECT_ROLES = [
  { value: 'project_admin', label: 'Admin' },
  { value: 'project_editor', label: 'Editor' },
  { value: 'project_viewer', label: 'Viewer' },
]

const onSearchSelect = (searchMember) => {
  const searchIds = searchAddedMembers.value.map(m => m.member_id)
  if (searchIds.includes(searchMember.member_id)) return

  searchAddedMembers.value.push({
    member_id: searchMember.member_id,
    display_name: searchMember.display_name || `${searchMember.first_name || ''} ${searchMember.last_name || ''}`.trim(),
    first_name: searchMember.first_name || '',
    last_name: searchMember.last_name || '',
    avatar_url: searchMember.avatar_url,
    email: searchMember.email,
    phone: searchMember.phone,
    role: 'project_editor',
    verified: searchMember.verified,
  })
}

const removeSearchMember = (index) => {
  searchAddedMembers.value.splice(index, 1)
}

const updateSearchMemberRole = (index, role) => {
  searchAddedMembers.value[index].role = role
}

const showManualEntry = ref(false)
const manualRole = ref('project_editor')
const manualInitialContact = ref('')

const onAddNew = (text) => {
  manualInitialContact.value = text
  showManualEntry.value = true
}

const onManualSubmit = (values, role) => {
  searchAddedMembers.value.push({
    member_id: null,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    phone: values.phone,
    avatar_url: null,
    display_name: generateDisplayName(values.first_name, values.last_name),
    role,
  })

  showManualEntry.value = false
  manualRole.value = 'project_editor'
}

const onManualCancel = () => {
  showManualEntry.value = false
}

const workflowChoices = [
  { label: 'Default', value: 'Default' },
  { label: 'Fisheries', value: 'Fisher' },
  { label: 'Worker Welfare', value: 'Worker Welfare' }
]

const isoToEmoji = (isoCode) => {
  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

const defaultParentId = props.edit
  ? (props.project.parent_id ? props.project.parent_id.toString() : null)
  : (props.project.project_id ? props.project.project_id.toString() : null)
const schema = ref(z.object({
  name: z.string().min(3).max(255).default(props.edit ? props.project.project_name : ''),
  description: z.string().max(255).nullable().optional().default(props.edit ? props.project.project_desc : ''),
  orgId: z.string().default(props.project ? props.project.organization_id.toString() : null),
  parentId: z.string().nullable().optional().default(defaultParentId),
  country: z.string().nullable().default(null),
  emoji: z.string().optional().nullable().default(props.edit ? props.project.emoji : null),
  workflow: z.string().optional().default(props.edit ? props.project.ui : 'Default'),
  placeDetails: z.object({
    place_id: z.number(),
    country_code: z.string(),
    localname: z.string(),
  }).optional().nullable().default(null),
}))

const onSubmit = async (values) => {
  isSubmiting.value = true
  try {
    if (!props.edit) {
      const newProject = await store.dispatch('project/createProjectRequest', {
        parentId: values.parentId && values.parentId !== '' && values.parentId !== 'none' ? values.parentId : null,
        name: values.name,
        description: values.description,
        orgId: values.orgId,
        country: values.country,
        ui: values.workflow,
        emoji: values.emoji || null,
        member: member.value
      })

      const newProjectId = newProject?.project?.project_id

      if (!newProjectId) {
        isSubmiting.value = false
        return
      }

      // Save boundary if location data is present
      if (isRegion.value && (values.placeDetails || selectedGeometry.value)) {
        await store.dispatch('boundary/createBoundary', {
          projectId: newProjectId,
          geometry: selectedGeometry.value?.geometry || null,
          color: boundaryColor.value,
          placeDetails: values.placeDetails || null,
        })
      }

      for (const pMember of searchAddedMembers.value) {
        await store.dispatch('project/addInvite', {
          inviter_id: member.value.member_id,
          project_id: newProjectId,
          role_name: pMember.role,
          first_name: pMember.first_name,
          last_name: pMember.last_name,
          email: pMember.email,
          phone: pMember.phone,
        })
      }
    } else {
      await store.dispatch('project/updateProject', {
        project_id: props.project.project_id,
        parentId: values.parentId && values.parentId !== '' && values.parentId !== 'none' ? values.parentId : null,
        name: values.name,
        description: values.description,
        orgId: values.orgId,
        country: values.country,
        ui: values.workflow,
        emoji: values.emoji || null,
        member: member.value
      })

      // Save/update boundary
      if (isRegion.value && (values.placeDetails || selectedGeometry.value || currentBoundary.value)) {
        const placeDetails = values.placeDetails || currentBoundary.value?.properties?.place_details || null
        await store.dispatch('boundary/updateBoundary', {
          projectId: props.project.project_id,
          geometry: selectedGeometry.value?.geometry || currentBoundary.value?.geometry || null,
          color: boundaryColor.value,
          placeDetails: placeDetails || null,
        })
      } else if (!isRegion.value && currentBoundary.value) {
        await store.dispatch('boundary/deleteBoundary', props.project.project_id)
      }
    }

    if (values.parentId && values.parentId !== '' && values.parentId !== 'none') {
      router.push({ name: 'ProjectHome', params: { project_id: values.parentId } })
    } else {
      router.push({ name: 'Dashboard' })
    }

    await store.dispatch('project/fetchProjects')
  } finally {
    isSubmiting.value = false
  }
}

const onFeatureSelected = async (feature, setValuesFunc) => {
  if (!feature) return

  if (feature.properties?.isSearch && feature.properties?.placeId) {
    const details = await getPlaceDetails(feature.properties.placeId)
    if (!details) return

    setValuesFunc({
      name: props.edit ? props.project.project_name : details.localname,
      emoji: isoToEmoji(details.country_code),
      placeDetails: {
        place_id: details.place_id,
        country_code: details.country_code,
        localname: details.localname,
      }
    })
  } else if (feature.properties?.isFromFile) {
    setValuesFunc({
      name: props.edit ? props.project.project_name : (feature.properties.name || ''),
      placeDetails: null
    })
  } else {
    // Drawn feature - no place details, just select the geometry
    setValuesFunc({ placeDetails: null })
  }
}

const handleLocationChange = () => {
  isRegion.value = !isRegion.value
  if (!isRegion.value) {
    selectedGeometry.value = null
  }
}

onMounted(async () => {
  if (props.edit && props.project?.project_id) {
    const boundary = await store.dispatch('boundary/fetchProjectBoundary', props.project.project_id)
    if (boundary) {
      currentBoundary.value = boundary
      boundaryColor.value = boundary.properties?.color || '#0059ff'
      // Backward compat: pre-fill emoji from boundary country_code if project has no emoji yet
      if (!props.project.emoji && boundary.properties?.place_details?.country_code) {
        formRef.value?.setFieldValue('emoji', isoToEmoji(boundary.properties.place_details.country_code))
      }
      if (boundary.properties?.place_details || boundary.geometry) {
        isRegion.value = true
      }
      if (boundary.geometry) {
        selectedGeometry.value = autoSimplifyFeature({
          type: 'Feature',
          id: 'boundary-' + props.project.project_id,
          geometry: boundary.geometry,
          properties: {
            name: boundary.properties?.place_details?.localname || 'Project Boundary',
            isBoundary: true,
            color: boundaryColor.value,
          }
        })
      }
    }
  }
})

watch(() => props.project, (value) => {
  schema.value = schema.value.extend({
    orgId: z.string().default(value.organization_id.toString()),
    parentId: z.string().nullable().optional().default(value.project_id ? value.project_id.toString() : null),
  })
}, { deep: true })
</script>

<template>
  <Form
    ref="formRef"
    :key="project.project_id ? project.project_id : null"
    class="space-y-3"
    v-slot="{ setValues }"
    :validation-schema="toTypedSchema(schema)"
    @submit="onSubmit"
  >
  <FormField v-if="project" v-slot="{ value, componentField }" name="orgId">
    <FormItem>
      <FormLabel>Organization</FormLabel>
      <FormControl>
        <span class="text-gray-500">
          {{ orgs.find(i => i.organization_id === project.organization_id)?.organization_name }}
        </span>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <FormField v-if="!edit || (edit && project.parent_id && !!flatedProjects.find(i => i.project_id === project.parent_id))" v-slot="{ value, componentField }" name="parentId">
    <FormItem>
      <FormLabel>Parent Project <span class="text-gray-500">(Optional)</span></FormLabel>
      <FormControl>
        <Select v-bind="componentField">
          <SelectTrigger>
            <SelectValue placeholder="Select a parent project (optional)" />
          </SelectTrigger>
          <SelectContent class="max-h-[300px]">
            <SelectGroup>
              <SelectItem value="none">
                <span class="text-gray-500">No parent project</span>
              </SelectItem>
              <SelectItem
                v-for="p of treeProjects"
                :key="p.project_id"
                :value="`${p.project_id}`"
                :style="{ paddingLeft: `${32 + p._level * 16}px` }"
              >
                {{p.project_name}}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <div>
    <div class="flex items-center">
      <Switch :model-value="isRegion" @update:model-value="handleLocationChange" />
      <Label class="ml-2">Location</Label>
    </div>
    <div v-if="isRegion" class="mt-2 border border-gray-200 rounded p-2">
      <FeatureSelection
        v-model:selectedGeometry="selectedGeometry"
        :color="boundaryColor"
        :allowed-draw-modes="['polygon']"
        @select="(feature) => onFeatureSelected(feature, setValues)"
      />

      <!-- Current Location (edit mode) -->
      <div v-if="currentBoundary?.properties?.place_details && edit" class="mt-2">
        <h3 class="mb-1">Current Location</h3>
        <div class="bg-white border border-gray-200 rounded-md p-2">
          <h4 class="text-sm text-gray-600 mb-0.5">
            {{ currentBoundary.properties.place_details.localname }}
          </h4>
          <ul class="text-gray-400 text-xs">
            <li>Country: {{ isoToEmoji(currentBoundary.properties.place_details.country_code) }}</li>
            <li>Place ID: {{ currentBoundary.properties.place_details.place_id }}</li>
          </ul>
        </div>
      </div>

      <!-- Boundary Color -->
      <div class="mt-2">
        <label class="text-sm font-medium">Boundary Color</label>
        <div class="flex items-center gap-2 mt-1">
          <ColorPicker v-model="boundaryColor" />
        </div>
      </div>
    </div>
  </div>

  <FormField v-slot="{ componentField }" name="name" class="flex-1">
    <FormItem>
      <FormLabel>Name</FormLabel>
      <FormControl>
        <div class="flex items-center gap-2">
          <FormField v-slot="{ value: emojiValue }" name="emoji">
            <EmojiPicker :modelValue="emojiValue" @update:modelValue="(v) => setValues({ emoji: v })" />
          </FormField>
          <Input v-bind="componentField" class="flex-1" />
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <FormField v-slot="{ componentField }" name="description">
    <FormItem>
      <FormLabel>Description <span class="text-gray-500">(Optional)</span></FormLabel>
      <FormControl>
        <Input v-bind="componentField" />
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <FormField v-slot="{ componentField }" name="workflow">
    <FormItem>
      <FormLabel>Workflow</FormLabel>
      <FormControl>
        <Select v-bind="componentField">
          <SelectTrigger>
            <SelectValue placeholder="Select a workflow" />
          </SelectTrigger>
          <SelectContent class="max-h-[300px]">
            <SelectGroup>
              <SelectItem
                v-for="(w, index) of workflowChoices"
                :key="index"
                :value="w.value"
              >
                {{w.label}}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <div v-if="!edit">
    <Label class="mb-1">Invite members</Label>

    <MemberSearchInput
      v-if="currentOrgId"
      :org-id="currentOrgId"
      placeholder="ex. Maria, john@acme.com, or +1234..."
      @select="onSearchSelect"
      @add-new="onAddNew"
    />

    <MemberManualEntryForm
      v-if="showManualEntry"
      v-model:role="manualRole"
      :roles="PROJECT_ROLES"
      default-role="project_editor"
      mode="emailOrPhone"
      :initial-contact="manualInitialContact"
      class="mt-2"
      @submit="onManualSubmit"
      @cancel="onManualCancel"
    />

    <div class="mt-2">
      <MemberInviteList
        :rows="searchAddedMembers"
        :roles="PROJECT_ROLES"
        @remove="removeSearchMember"
        @update:role="updateSearchMemberRole"
      />
    </div>
  </div>

  <div v-if="edit" class="flex flex-col gap-1">
    <router-link
      :to="{ name: 'ProjectMembers', params: { project_id: project.project_id } }"
      class="text-sm text-blue-600 hover:underline"
    >
      Edit members
    </router-link>
    <router-link
      :to="{ name: 'ProjectTemplates', params: { project_id: project.project_id } }"
      class="text-sm text-blue-600 hover:underline"
    >
      Edit templates
    </router-link>
  </div>

  <div class="flex items-center justify-end">
    <Button type="submit" :disabled="isSubmiting">
      <Spinner v-if="isSubmiting" class="w-4 h-4" />
      <span v-else>{{ edit ? 'Save' : 'Create' }}</span>
    </Button>
  </div>
</Form>
</template>
